import { type z } from 'zod'

import { schemas } from '~interface/zod-schema'

import { type FormStepId, type InputStepId, type SectionStepId } from './idRegistry'

// Values
export const FormNodeType = schemas.FormNodeCreateTypeEnum
export const InputStyle = schemas.InputStyleEnum
export const FormStyleElementEnum = schemas.FormStyleElementTypeEnum

// Types
export type FormNode = z.infer<typeof schemas.FormNodeCreate>
export type FormNodeTypeEnum = z.infer<typeof schemas.FormNodeCreateTypeEnum>
export type FormNodeIdType = FormStepId | SectionStepId | InputStepId
export type FormDataNode = z.infer<typeof schemas.FormDataNode>
export type InputStyleEnum = z.infer<typeof schemas.InputStyleEnum>
export type FormStyleElement = z.infer<typeof schemas.FormStyleElement>
export type FormStyleElementType = z.infer<typeof schemas.FormStyleElementTypeEnum>

export type ChargeSubStepCreate = z.infer<typeof schemas.ChargeSubStepCreate>
export type CopySectionSubStepViewRequestData = z.infer<
  typeof schemas.CopySectionSubStepViewRequestData
>
export type CreateFormStyleElementViewRequestData = z.infer<
  typeof schemas.CreateFormStyleElementViewRequestData
>
export type CreateInputStepViewRequestData = z.infer<typeof schemas.CreateInputStepViewRequestData>
export type CreateSectionStepViewRequestData = z.infer<
  typeof schemas.CreateSectionStepViewRequestData
>
export type CreateTagBlockViewRequestData = z.infer<typeof schemas.CreateTagBlockViewRequestData>
export type CreateRoleBlockViewRequestData = z.infer<typeof schemas.CreateRoleBlockViewRequestData>
export type CreateCommunicationBlockViewRequestData = z.infer<
  typeof schemas.CreateCommunicationBlockViewRequestData
>
export type CreateDocumentBlockViewRequestData = z.infer<
  typeof schemas.CreateDocumentGenerationBlockViewRequestData
>
export type EditTagBlockViewRequestData = z.infer<typeof schemas.EditTagBlockViewRequestData>
export type EditDemoBlockViewRequestData = z.infer<typeof schemas.EditDemoBlockViewRequestData>
export type EditAPIAutomationBlockViewRequestData = z.infer<
  typeof schemas.EditAPIAutomationBlockViewRequestData
>
export type EditRedactionBlockViewRequestData = z.infer<
  typeof schemas.EditRedactionBlockViewRequestData
>
export type EditLicenseBlockViewRequestData = z.infer<
  typeof schemas.EditLicenseBlockViewRequestData
>
export type EditRoleBlockViewRequestData = z.infer<typeof schemas.EditRoleBlockViewRequestData>
export type EditCommunicationBlockViewRequestData = z.infer<
  typeof schemas.EditCommunicationBlockViewRequestData
>
export type CommunicationRecipientPayload = z.infer<typeof schemas.CommunicationRecipientPayload>
export type DeleteInputStepViewRequestData = z.infer<
  typeof schemas.DeleteInputSubstepViewRequestData
>
export type DeleteSectionSubstepViewRequestData = z.infer<
  typeof schemas.DeleteSectionSubstepViewRequestData
>
export type DeleteFormStyleElementViewRequestData = z.infer<
  typeof schemas.DeleteFormStyleElementViewRequestData
>
export type EditBillStepViewRequestData = z.infer<typeof schemas.EditBillStepViewRequestData>
export type EditDateTimeStepViewRequestData = z.infer<
  typeof schemas.EditDateTimeStepViewRequestData
>
export type EditFormStepViewRequestData = z.infer<typeof schemas.EditFormStepViewRequestData>
export type EditIdentityStepViewRequestData = z.infer<
  typeof schemas.EditIdentityStepViewRequestData
>
export type EditFileStepViewRequestData = z.infer<typeof schemas.EditFileStepViewRequestData>
export type EditLocationStepViewRequestData = z.infer<
  typeof schemas.EditLocationStepViewRequestData
>
export type EditProcessStageViewRequestData = z.infer<
  typeof schemas.EditProcessStageViewRequestData
>
export type EditNumberStepViewRequestData = z.infer<typeof schemas.EditNumberStepViewRequestData>
export type EditSignatureStepViewRequestData = z.infer<
  typeof schemas.EditSignatureStepViewRequestData
>
export type EditFormStyleElementRequestData = z.infer<
  typeof schemas.EditFormStyleElementViewRequestData
>
export type EditInputStepViewRequestData = z.infer<typeof schemas.EditInputStepViewRequestData>
export type EditSectionSubStepViewRequestData = z.infer<
  typeof schemas.EditSectionSubStepViewRequestData
>
export type EditStepViewRequestData = z.infer<typeof schemas.EditStepViewRequestData>
export type DeleteStepAssignmentViewRequestData = z.infer<
  typeof schemas.DeleteStepAssignmentViewRequestData
>

export type VisualGroup = {
  type: FormNodeTypeEnum
  nodes: Array<SectionStepId | InputStepId>
}

export enum FormEditingStatus {
  preSubmit = 'preSubmit',
  submitted = 'submitted',
  editing = 'editing',
}

export enum StepType {
  form = 'form',
  bill = 'bill',
}

export function getFormEditingStatus(isEditing: boolean, completed: boolean): FormEditingStatus {
  if (isEditing) {
    return FormEditingStatus.editing
  } else if (completed) {
    return FormEditingStatus.submitted
  } else {
    return FormEditingStatus.preSubmit
  }
}

export function getReadOnlyStatus(
  formEditingStatus: FormEditingStatus,
  applyView: boolean,
  inReview: boolean,
): boolean {
  if (
    formEditingStatus === FormEditingStatus.editing ||
    formEditingStatus === FormEditingStatus.preSubmit
  ) {
    return false
  } else if (formEditingStatus === FormEditingStatus.submitted && !applyView) {
    return true
  } else if (formEditingStatus === FormEditingStatus.submitted && applyView) {
    return !inReview
  }
  return true // shouldn't hit this
}

export function determineVisualGroups(
  sortedChildren: (SectionStepId | InputStepId)[],
  nodeInfoDict: Record<FormStepId | SectionStepId | InputStepId, FormNode>,
): VisualGroup[] {
  let currentGroup: Array<SectionStepId | InputStepId> = []
  const groups: VisualGroup[] = []
  sortedChildren.forEach((childId: SectionStepId | InputStepId, index) => {
    const childNode = nodeInfoDict[childId]
    if (!childNode) return
    if (childNode.type === FormNodeType.enum.input) {
      currentGroup.push(childId)
      // If this is the last child or next child is a section, finish the input group
      const isLastChild = index === sortedChildren.length - 1
      const nextChild = !isLastChild ? nodeInfoDict[sortedChildren[index + 1]] : null
      if (isLastChild || nextChild?.type === FormNodeType.enum.section) {
        groups.push({ type: FormNodeType.enum.input, nodes: [...currentGroup] })
        currentGroup = []
      }
    } else if (
      childNode.type === FormNodeType.enum.section &&
      (childNode.shouldRender === null ||
        Object.values(childNode.shouldRender).some((v) => v === true))
    ) {
      // If we have pending inputs, add them as a group
      if (currentGroup.length > 0) {
        groups.push({ type: FormNodeType.enum.input, nodes: [...currentGroup] })
        currentGroup = []
      }
      groups.push({ type: FormNodeType.enum.section, nodes: [childId] })
    }
  })
  return groups
}
